import React, { useMemo, useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';
import { flowType } from '@utility/userInfo';
import { getObjectValue } from '@utility/utills';
import { safeJsonParse } from '@utility/helperFunction';
import { ReactComponent as Delete } from '@assets/images/delete.svg';
import store from '@store';
import { APP_CONFIG } from '@constants';
import { ReactComponent as VerticalEllipsis } from '@assets/images/verticalEllipsis.svg';
import { TouchableButton } from '@components/common/Touchable';
import style from './CartButtons.module.scss';
import { pendingItemList, returnSentItems } from '../CartItems/CartHelper';
import { LABEL } from '../../../../veriables';
import CartActionPopUpV2 from './CartActionPopUpV2';
import Loader from '@components/common/Loader/newLoaderModal';
import {
  changeCheckOrdersTab,
  postDiscount,
  updateSeatInfo,
  setSeat,
} from '@reducers/orders';
import { setErrorPopUp } from '@reducers/notification';
import { sendAllItems, deleteAllItems, applyHoldItems } from './CartActionHandler';
import { HoldItemsModal } from '../../../../components/HoldController/index.jsx';
import { repeatSelectedItems } from '../MenuCartBarHandler';
import { addInProcessItemsToCart } from '../../MenuOrderHandler';
import {
  payRoute,
  speedOrderRouteToPay,
  stageRoute,
  finaliseOrder,
  handleCashPayment,
} from './CartPaymentHandler';
import { footerMenuStyleGenerator } from './CartButtonHelper';
const CashPay = lazy(() => import('@components/checksCommon/cashPayment'));
import SendOrderMode from './SendOrderMode';
import DiscountFeature from '@components/OrderController/DiscountFeature';
import MoveItemModal from '../Modals/MoveItemModal';
import getDeviceType from '@utility/deviceType';
import ToGoModeActions from './ToGoModeActions';
import CounterModeActions from './CounterModeActions';
import ensureCardReaderConnection from '@utility/ensureCardReaderConnection';

const whiteFontStyle = {
  color: 'rgb(255, 255, 255)',
  fontSize: '13.5px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  lineHeight: 1,
  textAlign: 'center',
  overflow: 'visible',
};

const CartButtons = ({
  isCheckBoxClicked,
  checkedItems,
  resetCheckBoxState,
  ...props
}) => {
  const {
    cart,
    serverAppTheme,
    position,
    menuItemsHashTable,
    changeCheckOrdersTab,
    speedOrderStatus,
    deviceInfo,
    seat,
    auth,
    userInfo,
    discounts,
    loading,
    postDiscount,
    updateSeatInfo,
    setSeat,
    setErrorPopUp,
    handleManagerAuthentication,
    enableItemHold,
  } = props; // Redux Imports

  const [actionType, setActionType] = useState(null);
  const navigate = useNavigate();
  const orderSendModeBtnRef = useRef(null);
  const isServerApp = useMemo(() => flowType() === 'serverApp', []);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSendOrderMode, setShowSendOrderMode] = useState(false);
  const deviceType = getDeviceType();
  const defaultMoveState = { isOpen: false, data: [] };
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(defaultMoveState);
  const defaultHoldState = { isOpen: false, items: [] };
  const [holdModalState, setHoldModalState] = useState(defaultHoldState);
  const [cardReaderConnectionState, setCardReaderConnectionState] = useState({
    isLoading: false,
    message: '',
  });
  const [isCashPay, setIsCashPay] = useState(false);
  const [shouldNavigateOnClose, setShouldNavigateOnClose] = useState(false);

  useEffect(() => {
    const handleClickOutside = event => {
      const sendOrderModeDiv = document.getElementById('sendOrderModeDiv');
      if (
        showSendOrderMode &&
        sendOrderModeDiv &&
        !sendOrderModeDiv.contains(event.target) &&
        !orderSendModeBtnRef.current?.contains(event.target)
      ) {
        setShowSendOrderMode(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSendOrderMode]);

  useEffect(() => {
    if (showSendOrderMode) {
      setShowSendOrderMode(false);
    }
  }, [cart]);

  const [driveThruSelections, setDriveThruSelections] = useState({
    lane: null,
    vehicleType: null,
    vehicleColor: null,
  });
  const [actionFn, setActionFn] = useState(null);

  // TODO: Move this to AppSelector later
  // Used in CartItems
  let pendingItems = useMemo(
    () => pendingItemList(cart, position, menuItemsHashTable),
    [cart, position, menuItemsHashTable]
  );
  const selectedItems = useMemo(
    () => pendingItems?.reduce((sum, item) => sum + Number(item?.quantity || 0), 0) ?? 0,
    [pendingItems]
  );
  const isWalkUp = useMemo(
    () => getObjectValue(() => seat && seat.sourceType === 7, false),
    [seat]
  );
  const isDefaultMode = useMemo(
    () =>
      getObjectValue(
        () =>
          !isWalkUp &&
          !speedOrderStatus &&
          (!deviceInfo.deviceGroup ||
            [0].includes(deviceInfo.deviceGroup.startMode)),
        false
      ),
    [deviceInfo, isWalkUp, speedOrderStatus]
  );
  const isPreAuth = deviceInfo?.deviceGroup?.paymentTypes?.preAuth;
  const isDriveThru = useMemo(
    () =>
      getObjectValue(
        () => isServerApp && [3].includes(deviceInfo.deviceGroup.startMode),
        false
      ),
    [isServerApp, deviceInfo]
  );
  const isCounter = useMemo(
    () =>
      getObjectValue(
        () => isServerApp && [1].includes(deviceInfo.deviceGroup.startMode),
        false
      ),
    [isServerApp]
  );
  const isPaymentDisabled =
    isDefaultMode &&
    (seat?.items?.length && seat?.subTotalAmount === seat?.discountAmount
      ? false
      : Number(seat?.unpaidAmount) === 0 && Number(seat?.discountAmount) === 0);

  const disableIfNoPendingItems = !pendingItems || pendingItems?.length == 0;

  const checkOrderItems = useMemo(
    () => seat?.orders?.[0]?.items ?? [],
    [seat]
  );

  // All-seats pending count — used for delete button label and disabled state.
  const allSeatsPendingCount = useMemo(
    () =>
      (cart ?? []).reduce((sum, item) => {
        if (!item.pending || (item.isLinked && menuItemsHashTable?.[item?.menuItemId]?.cardType !== 1)) return sum;
        return sum + (Number(item?.quantity) || 0);
      }, 0),
    [cart, menuItemsHashTable]
  );
  const hasAnyPending = allSeatsPendingCount > 0;
  const unheldCount = useMemo(
    () =>
      (pendingItems ?? []).reduce((sum, item) => {
        const isHeld = !!checkOrderItems.find(c => c.id === item.id)?.sendDate;
        return !isHeld ? sum + (Number(item?.quantity) || 0) : sum;
      }, 0),
    [pendingItems, checkOrderItems]
  );

  // Check ALL seats in the check (not just the selected position) for unheld pending items.
  // Send All should be enabled whenever any seat has items to send.
  const allSeatsUnheldCount = useMemo(
    () =>
      (cart ?? []).reduce((sum, item) => {
        if (!item.pending || (item.isLinked && menuItemsHashTable?.[item?.menuItemId]?.cardType !== 1)) return sum;
        const isHeld = !!checkOrderItems.find(c => c.id === item.id)?.sendDate;
        return !isHeld ? sum + (Number(item?.quantity) || 0) : sum;
      }, 0),
    [cart, checkOrderItems, menuItemsHashTable]
  );
  const hasUnheld = allSeatsUnheldCount > 0;

  const checkedItemsCount = useMemo(() => {
    if (props.isPendingList) {
      return pendingItems
        .filter(item => checkedItems[item.internalId])
        .reduce((sum, item) => sum + (item.quantity || 0), 0);
    }
    const sentItems = returnSentItems(seat, position);
    return sentItems
      .filter(item => checkedItems[item.id])
      .reduce((sum, item) => sum + (item.quantity || 0), 0);
  }, [checkedItems, isCheckBoxClicked, pendingItems, props.isPendingList, seat, position]);

  const isPackItem = item =>
    menuItemsHashTable?.[item?.menuItemId]?.minOrderQuantity > 0 ||
    menuItemsHashTable?.[item?.menuItemId]?.cardType === 1 ||
    menuItemsHashTable?.[item?.menuItemId]?.cardType === 2;

  const hasPackItem = useMemo(() => {
    if (props.isPendingList) {
      return pendingItems
        .filter(item => checkedItems[item.internalId])
        .some(isPackItem);
    }
    return returnSentItems(seat, position)
      .filter(item => checkedItems[item.id])
      .some(isPackItem);
  }, [checkedItems, pendingItems, menuItemsHashTable, props.isPendingList, seat, position]);
  // TODO: Later check this
  // !activateGiftCards(props.seat)) ||
  // (isOdCheck && !props.auth.nonIntegratedPos)
  const footerMenuStyle = useMemo(() => {
    return footerMenuStyleGenerator({
      serverAppTheme,
      isDriveThru,
      isPreAuth,
      isPaymentDisabled,
      cart,
      isWalkUp,
    });
  }, [serverAppTheme, isDriveThru, isPreAuth, isPaymentDisabled, cart]);

  const discountVisibility = () => {
    let visibleDiscountName = false;

    discounts?.forEach(item => {
      if (visibleDiscountName) return;

      if (item?.jobVisibility && item.jobVisibility.none) {
        visibleDiscountName = false;
        return;
      }

      let jobId = '';

      switch (userInfo?.jobTypeId) {
        case 1:
          jobId = 'manager';
          break;
        case 2:
          jobId = 'server';
          break;
        case 3:
          jobId = 'bartender';
          break;
        case 4:
          jobId = 'cashier';
          break;
        default:
          jobId = '';
      }

      Object.entries(item.jobVisibility).forEach(([key, value]) => {
        if (key === jobId && value === true) {
          visibleDiscountName = true;
        }
      });
    });

    return visibleDiscountName;
  };

  const showDiscountButton = useMemo(
    () =>
      isServerApp &&
      (isDefaultMode || isWalkUp) &&
      seat?.totalAmount?.toFixed(2) > 0 &&
      userInfo?.jobTypeId !== null &&
      userInfo?.jobTypeId !== 0 &&
      userInfo?.jobTypeId < 5 &&
      discounts !== null &&
      discountVisibility(),
    [
      isServerApp,
      isDefaultMode,
      isWalkUp,
      seat?.totalAmount,
      userInfo?.jobTypeId,
      discounts,
    ]
  );

  const handleHoldModalClick = () => {
    const selectedItemsList = pendingItems.filter(
      item => checkedItems[item.internalId]
    );
    // Keep unheld items first, held (items with sendDate) last — matching cart display order.
    const unheld = selectedItemsList.filter(item => !checkOrderItems.find(c => c.id === item.id)?.sendDate);
    const held = selectedItemsList.filter(item => !!checkOrderItems.find(c => c.id === item.id)?.sendDate);
    setHoldModalState({ isOpen: true, items: [...unheld, ...held] });
  };

  const handleApplyHold = (itemsWithHoldTime) => {
    setIsLoading(true);
    applyHoldItems(itemsWithHoldTime, {
      onSuccess: () => {
        setIsLoading(false);
        setHoldModalState(defaultHoldState);
        resetCheckBoxState();
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  const handleMoveModalClick = () => {
    let selectedItemsList = [];
    if (props.isPendingList) {
      selectedItemsList = pendingItems.filter(
        item => checkedItems[item.internalId]
      );
    } else {
      const allSentItems = returnSentItems(seat, position);
      selectedItemsList = allSentItems.filter(item => checkedItems[item.id]);
    }
    setIsMoveModalOpen({ isOpen: true, data: selectedItemsList });
  };

  const onCashPaymentConfirm = () => {
    setShouldNavigateOnClose(true);
    return handleCashPayment();
  };

  const handleCashModalClose = () => {
    setIsCashPay(false);
    if (shouldNavigateOnClose) {
      navigate(`${APP_CONFIG.BASE_URL}/serverApp/checks`);
      setShouldNavigateOnClose(false);
    }
  };

  const handlePayClick = useCallback(
    async (data, currentPage) => {
      if (cardReaderConnectionState.isLoading) {
        return;
      }

      setCardReaderConnectionState({
        isLoading: true,
        message: LABEL().CARD_READER_CHECKING_CONNECTION,
      });

      try {
        const cardReaderResult = await ensureCardReaderConnection({
          maxReconnectAttempts: 2,
          skipConnectionCheck: Boolean(deviceInfo?.deviceGroup?.serverAppSettings?.disablePayment) || deviceInfo?.deviceGroup?.paymentTypes?.creditCard === false,
          onProgress: message => {
            console.log('ensureCardReaderConnection - onProgress:', message);
            setCardReaderConnectionState({
              isLoading: true,
              message,
            });
          },
        });

        if (cardReaderResult.ok) {
          console.log(
            'ensureCardReaderConnection - succeeded:',
            cardReaderResult.message || cardReaderResult
          );
          // pay(data, currentPage);
          let cb;
          const type =
            (isCounter || isDriveThru || isWalkUp || deviceType?.isCallCenter || deviceType?.isSpeedOrderMode)
              ? 'speedPay' : deviceType?.isTogo ? "finalizeOrder"
                : isPaymentDisabled
                  ? ''
                  : 'pay';

          switch (type) {
            case 'speedPay':
              cb = (Cart, checkHeader) =>
                speedOrderRouteToPay(checkHeader, navigate);
              break;
            case 'pay':
              cb = (Cart, checkHeader) =>
                payRoute(checkHeader, navigate, () => setIsCashPay(true));
              break;
            case 'stage':
              cb = (Cart, checkHeader) => stageRoute(checkHeader, navigate);
              break;
            case 'finalizeOrder':
              cb = () => {
                finaliseOrder({
                  isDriveThru: false,
                  isCounter: false,
                  isTogo: true,
                  isCallCenter: false,
                  driveThruSelections: {},
                  isSpeedOrderMode: false,
                  navigate: navigate,
                });
              }
              break;
            default:
              break;
          }
          if (deviceType?.isTogo) cb?.();
          else addInProcessItemsToCart(cb, cart);
          setIsLoading(false);
          window.sessionStorage.removeItem('menuScrollTop');
          return;
        }

        console.error(
          'ensureCardReaderConnection - failed:',
          cardReaderResult.errorMessage,
          cardReaderResult.error
        );

        setErrorPopUp({
          res: LABEL().CARD_READER_PAY_PREVENTION_ERROR,
          fullWidth: true,
        });
      } finally {
        setCardReaderConnectionState({
          isLoading: false,
          message: '',
        });
      }
    },
      [cardReaderConnectionState.isLoading, setErrorPopUp,cart]
  );

  const cartButtonBtn = [
    {
      name: `${!isNaN(allSeatsPendingCount) ? `(${allSeatsPendingCount})` : ''}`,
      img: (
        <Delete
          fill={
            allSeatsPendingCount > 0
              ? serverAppTheme?.btn_font_color
              : serverAppTheme?.btn_font_color_disabled
          }
          width={18}
          height={18}
        />
      ),
      className: cn('px-0 mr-3', deviceType?.isTogo ? 'col-12' : 'col-3'),
      style: {
        backgroundColor: !hasAnyPending
          ? 'rgb(177, 177, 177)'
          : 'rgb(116, 164, 255)',
        width: deviceType?.isTogo ? '100%' : '67px',
        height: '40px',
        borderRadius: '4px',
        padding: '2.5px 5px',
        ...whiteFontStyle,
        fontWeight: 400,
        color: !hasAnyPending
          ? serverAppTheme?.btn_font_color_disabled
          : serverAppTheme?.btn_font_color,
      },
      key: 'deleteAll',
      click: () => {
        deleteAllItems();
        changeCheckOrdersTab({ isPendingList: false });
      },
      show: true,
      confirm: true,
      disable: !hasAnyPending,
    },
    {
      name: `${LABEL().SEND_ALL} (${!isNaN(allSeatsUnheldCount) ? allSeatsUnheldCount : ''})`,
      className: cn('pr-0 pl-0', style.flexFill),
      style: {
        height: '40px',
        borderTopRightRadius: '0px',
        borderBottomRightRadius: '0px',
        background: !hasUnheld
          ? 'rgb(177, 177, 177)'
          : showSendOrderMode
            ? serverAppTheme?.sendOrderModeBg
            : serverAppTheme?.secondaryBtn,
        ...whiteFontStyle,
        fontWeight: 400,
        color: !hasUnheld
          ? serverAppTheme?.btn_font_color_disabled
          : serverAppTheme?.btn_font_color,
      },
      key: 'sendAll',
      click: () => {
        sendAllItems(actionType?.type, navigate, null, null, {
          onSuccess: () => changeCheckOrdersTab({ isPendingList: false }),
        });
      },
      show: !isCounter && !deviceType.isTogo && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
      confirm: true,
      disable: !hasUnheld,
    },
    {
      name: '',
      img: (
        <>
          <VerticalEllipsis
            fill={
              !hasUnheld
                ? serverAppTheme?.btn_font_color_disabled
                : serverAppTheme?.btn_font_color
            }
            width={25}
            height={25}
          />
        </>
      ),
      className: 'px-0 col-2',
      style: {
        background: !hasUnheld
          ? 'rgb(177, 177, 177)'
          : showSendOrderMode
            ? serverAppTheme?.sendOrderModeBg
            : serverAppTheme?.secondaryBtn,
      },
      key: 'orderSendMode',
      click: () => setShowSendOrderMode(prev => !prev),
      show: !isCounter && !deviceType.isTogo && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
      disable: !hasUnheld,
      confirm: false,
    },
  ];

  const job = (auth?.jobs ?? []).find(item => item?.id === userInfo?.jobId);
  const allowVoid = !!(auth?.nonIntegratedPos ? job?.allowVoid : false);

  const sentItemsAnchoredBtns = [
    {
      name: LABEL().MOVE,
      img: undefined,
      key: 'move',
      className: 'px-0 col-6',
      style: {
        backgroundColor: 'rgb(116, 164, 255)',
        height: '40px',
        borderRadius: '4px',
        padding: '2.5px 5px',
        ...whiteFontStyle,
      },
      // click: data => handleActionTypes(data, 'sentMove'),
      click: handleMoveModalClick,
      show: !deviceType.isTogo && !isCounter && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
    },
    {
      name: LABEL().REPEAT,
      img: undefined,
      key: 'repeat',
      className: 'pl-1 pr-0 col-6',
      style: {
        backgroundColor: 'rgb(116, 164, 255)',
        height: '40px',
        borderRadius: '4px',
        padding: '2.5px 5px',
        ...whiteFontStyle,
      },
      click: () => {
        repeatSelectedItems(checkedItems);
        resetCheckBoxState(false);
      },
      show: !deviceType.isTogo && !isCounter && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
    },
    {
      name: LABEL().VOID,
      img: undefined,
      key: 'void',
      className: 'px-0 col-6',
      style: {
        backgroundColor: 'rgb(116, 164, 255)',
        height: '40px',
        borderRadius: '4px',
        padding: '2.5px 5px',
        ...whiteFontStyle,
      },
      // click: data => {
      //   props.handleSingleItemVoidClick(data);
      // },
      click: () => handleManagerAuthentication(),
      show: !props.isPendingList && allowVoid && seat?.open !== false && !isCounter && !deviceType.isTogo && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
    },
    {
      name: `${LABEL().DELETE} (${checkedItemsCount})`,
      img: undefined,
      key: 'delete_many',
      className: hasPackItem ? 'px-0 col-12' : 'px-0 col-6',
      click: () => {
        const expandedCheckedItems = { ...checkedItems };
        pendingItems
          .filter(item => checkedItems[item.internalId])
          .forEach(item => {
            const uid =
              item?.uniqueIdForGameCardItem ||
              safeJsonParse(item?.modifierJson)?.uniqueIdForGameCardItem;
            if (uid) {
              cart.forEach(c => {
                const cUid =
                  c?.uniqueIdForGameCardItem ||
                  safeJsonParse(c?.modifierJson)?.uniqueIdForGameCardItem;
                if (cUid === uid) {
                  expandedCheckedItems[c.internalId] = true;
                  if (c.id) expandedCheckedItems[c.id] = true;
                }
              });
            }
          });
        deleteAllItems(expandedCheckedItems);
        resetCheckBoxState();
        changeCheckOrdersTab({ isPendingList: false });
      },
      style: {
        backgroundColor: 'rgb(116, 164, 255)',
        height: '40px',
        borderRadius: '4px',
        padding: '2.5px 5px',
        ...whiteFontStyle,
      },
      show: props.isPendingList,
      disable: false,
      confirm: true,
    },
    {
      name: `${LABEL().HOLD} (${checkedItemsCount})`,
      img: undefined,
      key: 'hold',
      className: 'pl-1 pr-0 col-6',
      style: {
        backgroundColor: 'rgb(116, 164, 255)',
        height: '40px',
        borderRadius: '4px',
        padding: '2.5px 5px',
        ...whiteFontStyle,
      },
      click: handleHoldModalClick,
      show: props.isPendingList && !deviceType.isTogo && !isCounter && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode && enableItemHold,
      disable: false,
      confirm: false,
    },
    {
      name: `${LABEL().SEND} (${checkedItemsCount})`,
      className: cn('pl-1 pr-0', style.flexFill),
      style: {
        height: '40px',
        borderRadius: '4px 0px 0px 4px',
        background: disableIfNoPendingItems
          ? 'rgb(177, 177, 177)'
          : showSendOrderMode
            ? serverAppTheme?.sendOrderModeBg
            : serverAppTheme?.secondaryBtn,
        ...whiteFontStyle,
        color: disableIfNoPendingItems
          ? serverAppTheme?.btn_font_color_disabled
          : serverAppTheme?.btn_font_color,
      },
      key: 'sendAll',
      click: () => {
        // Clear checkboxes immediately when user confirms send (before API resolves).
        resetCheckBoxState(false);
        sendAllItems(actionType?.type, navigate, null, checkedItems, {
          onSuccess: () => changeCheckOrdersTab({ isPendingList: false }),
        });
      },
      show: props.isPendingList && !isCounter && !deviceType.isTogo && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
      confirm: true,
      disable: disableIfNoPendingItems,
    },
    {
      name: '',
      img: (
        <>
          <VerticalEllipsis
            fill={
              selectedItems === 0
                ? serverAppTheme?.btn_font_color_disabled
                : serverAppTheme?.btn_font_color
            }
            width={25}
            height={25}
          />
        </>
      ),
      className: 'px-0',
      style: {
        background: disableIfNoPendingItems
          ? 'rgb(177, 177, 177)'
          : showSendOrderMode
            ? serverAppTheme?.sendOrderModeBg
            : serverAppTheme?.secondaryBtn,
        borderRadius: '0px 4px 4px 0px',
      },
      key: 'orderSendMode',
      click: () => setShowSendOrderMode(prev => !prev),
      show: props.isPendingList && !isCounter && !deviceType.isTogo && !deviceType?.isCallCenter && !deviceType?.isSpeedOrderMode,
      disable: disableIfNoPendingItems,
      confirm: false,
    },
  ];

  const payButton = [
    {
      name: deviceType?.isTogo
        ? LABEL().FINALIZE_ORDER
        : (deviceInfo?.deviceGroup?.serverAppSettings.disablePayment
          ? LABEL().SPLIT
          : LABEL().PAY +
          ` $${!isNaN(seat?.unpaidAmount)
            ? Number(seat?.unpaidAmount).toFixed(2)
            : ''
          } `),
      className: 'px-0 col-12 mt-1',
      style: {
        backgroundColor: 'rgb(177, 177, 177)',
        color: 'rgb(99, 99, 99)',
        ...footerMenuStyle.payButtonStyle,
      },
      key: 'payButton',
      click: () => {
        handlePayClick();
      },
      confirm: false,
      show: true,
      disable: deviceType?.isTogo ? cart?.length === 0 : isPaymentDisabled,
    },
  ];

  const hasAnyChecked = Object.values(checkedItems).some(Boolean);

  const hasCheckedDiscount = useMemo(() => {
    if (!hasAnyChecked || props.isPendingList) return false;
    const sentItems = returnSentItems(seat, position);
    return sentItems
      .filter(item => checkedItems[item.id] || checkedItems[item.discountId] || checkedItems[item.serviceChargeId] || checkedItems[item.internalId])
      .some(
        item =>
          item?.isDiscount ||
          item?.discountId ||
          (item?.discountName && !item?.menuItemId) ||
          item?.isServiceCharge ||
          item?.serviceChargeId ||
          (item?.serviceChargeName && !item?.menuItemId)
      );
  }, [checkedItems, hasAnyChecked, seat, position, props.isPendingList]);

  const buttonsToShow = hasAnyChecked
    ? hasCheckedDiscount
      ? sentItemsAnchoredBtns
        .filter(btn => btn.key === 'move' || (auth?.nonIntegratedPos && btn.key === 'void'))
        .map((btn, idx, arr) => ({
          ...btn,
          className:
            arr.length > 1
              ? idx === 0
                ? 'pr-1 pl-0 col-6'
                : 'pl-1 pr-0 col-6'
              : 'px-0 col-6',
        }))
      : hasPackItem
        ? props.isPendingList
          ? sentItemsAnchoredBtns.filter(btn => btn.key === 'delete_many')
          : []
        : sentItemsAnchoredBtns
    : cartButtonBtn;

  if (isLoading || cardReaderConnectionState.isLoading) {
    return (
      <div
        className={cn(
          'h-100 d-flex justify-content-center align-items-center p-10 fs_18 fw-400 text-center'
        )}
      >
        <Loader message={
          cardReaderConnectionState.isLoading
            ? cardReaderConnectionState.message
            : ''}
        />
      </div>
    );
  }

  if (deviceType?.isTogo) {
    return <ToGoModeActions isCheckBoxClicked={isCheckBoxClicked} checkedItems={checkedItems} resetCheckBoxState={resetCheckBoxState} />;
  }

  if (isCounter || deviceType?.isCallCenter || deviceType?.isSpeedOrderMode || isDriveThru) {
    return <CounterModeActions isCheckBoxClicked={isCheckBoxClicked} checkedItems={checkedItems} resetCheckBoxState={resetCheckBoxState} />;
  }

  return (
    <>
      {showSendOrderMode && (
        <SendOrderMode setShowSendOrderMode={setShowSendOrderMode} />
      )}
      {cartButtonBtn.length > 0 && (
        <div
          className={cn(
            'col-12 px-0 d-flex justify-content-start flex-wrap ',
            selectedItems === 0 && 'mt-0'
          )}
        >
          {buttonsToShow.map(btn => {
            return btn?.show ? (
              <div
                key={btn?.key}
                className={cn(btn.className)}
                ref={btn.key === 'orderSendMode' ? orderSendModeBtnRef : null}
              >
                <div>
                  <TouchableButton
                    onClick={event => {
                      event.preventDefault();
                      if (btn.confirm) {
                        window.sessionStorage.removeItem('itemCount');
                        let orderTypes = false;
                        if (seat?.sourceType != 11) {
                          for (
                            let index = 0;
                            index < auth?.orderTypes?.length;
                            index++
                          ) {
                            const element = auth?.orderTypes[index];
                            if (element.printRouting === true) {
                              orderTypes = true;
                            }
                          }
                        }
                        setActionFn(() => () => {
                          btn.click?.();
                        });
                        if (orderTypes === false) {
                          setIsOpen(true);
                          setActionType({
                            type: btn.key ?? 'sendAll',
                            data: {},
                          });
                          return;
                        }
                      } else {
                        btn.click?.();
                      }
                    }}
                    className={cn(
                      style.bottomBtn,'mb-2',
                      btn.key === 'sendAll' ? 'pl-2 pr-0' : 'px-0'
                    )}
                    disabled={btn.disable}
                    style={{
                      background:
                        btn.key === 'send' && serverAppTheme?.secondaryBtn,
                      width: '100%',
                      height: '40px',
                      ...btn.style,
                    }}
                    rippleEffect={true}
                  >
                    {btn?.img} {btn?.name}
                    {pendingItems?.filter(e => e.isCheck)?.length === 0 ? (
                      ''
                    ) : (
                      <input type="checkbox" checked />
                    )}
                  </TouchableButton>
                </div>
              </div>
            ) : (
              <></>
            );
          })}
        </div>
      )}
      <div
        className="col-12 px-0 mt-1 d-flex align-items-center"
        style={{ height: '40px' }}
      >
        {showDiscountButton && (
          <DiscountFeature
            seat={seat}
            serverAppTheme={serverAppTheme}
            discounts={discounts}
            deviceInfo={deviceInfo}
            userInfo={userInfo}
            postDiscount={postDiscount}
            updateSeatInfo={updateSeatInfo}
            setSeat={setSeat}
            setErrorPopUp={setErrorPopUp}
            loading={loading}
          />
        )}
        {payButton.map(btn => (
          <div key={btn?.key} style={{ flex: 1 }}>
            <TouchableButton
              onClick={e => {
                e.preventDefault();
                btn.click?.();
              }}
              className={cn('px-0', style.bottomBtn)}
              disabled={btn.disable}
              style={{
                background: (btn.key === 'send') ? serverAppTheme?.secondaryBtn : (deviceType?.isTogo && cart.length !== 0) && serverAppTheme?.enabledPayBtn,
                width: '100%',
                height: '40px',
                ...btn.style,
              }}
              rippleEffect={true}
            >
              {btn.img && btn.img} {btn.name}
            </TouchableButton>
          </div>
        ))}
      </div>
      {/* TODO: Integrate dynamic style with actionModal later*/}
      {isOpen && (
        <CartActionPopUpV2
          actionType={actionType}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          actionFn={actionFn}
        />
      )}
      {isMoveModalOpen.isOpen && (
        <MoveItemModal
          isOpen={isMoveModalOpen?.isOpen}
          data={isMoveModalOpen?.data}
          closeModal={() => {
            setIsMoveModalOpen(defaultMoveState);
            resetCheckBoxState();
          }}
        />
      )}
      {holdModalState.isOpen && (
        <HoldItemsModal
          isOpen={holdModalState.isOpen}
          items={holdModalState.items}
          checkOrders={seat?.orders}
          seat={seat}
          serverAppTheme={serverAppTheme}
          onClose={() => setHoldModalState(defaultHoldState)}
          onApplyHold={handleApplyHold}
        />
      )}
      <Suspense fallback={null}>
        {isCashPay && (
          <CashPay
            isOpen={isCashPay}
            cashPaymentCallBack={onCashPaymentConfirm}
            onClose={handleCashModalClose}
          />
        )}
      </Suspense>
    </>
  );
};

const mapStateToProps = ({
  orders,
  theme,
  oauth,
  notification,
  storeChecks,
}) => ({
  position: orders.position,
  serverAppTheme: theme.serverAppTheme,
  seat: orders.seats,
  menuItemsHashTable: orders.menuItemsHashTable,
  cart: orders.cart,
  speedOrderStatus: orders.speedOrderStatus,
  deviceInfo: oauth.deviceInformation,
  isPendingList: orders.isPendingList,
  userInfo: oauth.loggedInUserData,
  auth: oauth.authInfo,
  discounts: orders.discounts,
  loading: notification.loading || storeChecks.loading,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changeCheckOrdersTab,
      postDiscount,
      updateSeatInfo,
      setSeat,
      setErrorPopUp,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(CartButtons));
